import { defineStore } from "pinia";
import { ref, watch } from "vue";

import { NoSuccessError } from "@/backend/NoSuccessError";
import type { SightlineBlock, SightlineContext } from "@/backend/natsstubs/sightline/models";
import { sendSightlineMessage, stopSightlineMessage } from "@/backend/read/sightline";
import { captureException } from "@/error/sentry";
import { useGlobalI18n } from "@/translations/i18n";
import { asReadonly } from "@/utils/vue";

import { useBoardStore } from "./board";
import { toggleFunc } from "./helpers/toggle";
import { useSessionStore } from "./session";

interface UserMessage {
  role: "user";
  text: string;
}

interface AssistantMessage {
  role: "assistant";
  blocks: SightlineBlock[];
  isError?: boolean;
}

export type ChatMessage = UserMessage | AssistantMessage;

// The server-side conversation lives in a TTL'd store; holding on to an
// expired id would fail every follow-up.
function isConversationExpired(err: unknown): boolean {
  if (!(err instanceof NoSuccessError)) return false;
  const details = err.response.details as { reason?: string } | undefined;
  return details?.reason === "conversation_expired";
}

// Where the user is, taken at send time: one conversation can span several
// boards, and an answer takes long enough for the user to move on meanwhile.
function currentContext(): SightlineContext {
  const context: SightlineContext = {};
  const board = useBoardStore().board;
  if (board) {
    context.boardId = board.id;
  }
  const iteration = useSessionStore().currentIteration;
  if (iteration) {
    context.iterationId = iteration.id;
  }
  return context;
}

export const useSightlineStore = defineStore("sightline", () => {
  const { t } = useGlobalI18n();

  const isOpen = ref(false);
  const messages = ref<ChatMessage[]>([]);
  const transcriptId = ref(crypto.randomUUID());
  const pending = ref(false);

  // The epoch guard drops in-flight answers that belonged to a discarded
  // conversation.
  let epoch = 0;

  // Server-side conversation handle. In-memory only, so parallel tabs never
  // share a conversation; discarded together with the transcript.
  let conversationId: string | undefined;

  // Names the in-flight question so a stop cancels exactly it, nothing else.
  let pendingMessageId: string | undefined;

  function close() {
    isOpen.value = false;
    reset();
  }

  function reset() {
    stopPendingMessage();
    epoch += 1;
    messages.value = [];
    transcriptId.value = crypto.randomUUID();
    pending.value = false;
    conversationId = undefined;
  }

  // A minimized conversation must not resurface in another session.
  watch(() => useSessionStore().current.id, close);

  async function ask(question: string) {
    if (pending.value) return;
    const askEpoch = epoch;
    const messageId = crypto.randomUUID();
    pendingMessageId = messageId;
    messages.value.push({ role: "user", text: question });
    pending.value = true;
    try {
      const response = await sendSightlineMessage({
        text: question,
        messageId,
        conversationId,
        context: currentContext(),
      });
      if (askEpoch !== epoch) return;
      conversationId = response.conversationId ?? conversationId;
      messages.value.push({ role: "assistant", blocks: response.blocks });
    } catch (err) {
      if (isConversationExpired(err)) {
        if (askEpoch !== epoch) return;
        conversationId = undefined;
        messages.value.push({
          role: "assistant",
          blocks: [{ type: "text", text: t("sightline.conversationExpired") }],
          isError: true,
        });
        return;
      }
      if (askEpoch !== epoch) return;
      captureException(err);
      messages.value.push({
        role: "assistant",
        blocks: [{ type: "text", text: t("sightline.answerFailed") }],
        isError: true,
      });
    } finally {
      if (askEpoch === epoch) {
        pending.value = false;
        pendingMessageId = undefined;
      }
    }
  }

  function stop() {
    if (!pending.value) return;
    stopPendingMessage();
    // Unblock the UI before the publish round-trip; the epoch bump makes the
    // in-flight ask drop the server's eventual empty reply.
    epoch += 1;
    pending.value = false;
    messages.value.push({
      role: "assistant",
      blocks: [{ type: "text", text: t("sightline.stopped") }],
    });
  }

  function stopPendingMessage() {
    if (pendingMessageId === undefined) return;
    stopSightlineMessage(pendingMessageId).catch(captureException);
    pendingMessageId = undefined;
  }

  return {
    isOpen: asReadonly(isOpen),
    messages: asReadonly(messages),
    transcriptId: asReadonly(transcriptId),
    pending: asReadonly(pending),
    toggle: toggleFunc(isOpen),
    close,
    reset,
    ask,
    stop,
  };
});
